/**
 * Result type for business logic error handling.
 *
 * Pattern: Services return Result<T> for expected business errors.
 * System errors (DB down, S3 timeout) throw naturally and are caught
 * by the global error handler → 500.
 *
 * Usage:
 *   // In a service:
 *   async function getVisit(id: VisitId): Promise<Result<Visit>> {
 *       const visit = await db.query...;
 *       if (!visit) return err({ code: "NOT_FOUND", entity: "visit", id });
 *       return ok(visit);
 *   }
 *
 *   // In a route:
 *   const result = await getVisit(id);
 *   if (!result.ok) return mapErrorToHttp(reply, result.error);
 *   reply.send({ data: result.data });
 */
// ─── Constructors ─────────────────────────────────────────
export function ok(data) {
    return { ok: true, data };
}
export function err(error) {
    return { ok: false, error };
}
// ─── Convenience error constructors ───────────────────────
export const AppErrors = {
    notFound(entity, id) {
        return { code: "NOT_FOUND", entity, id };
    },
    conflict(message) {
        return { code: "CONFLICT", message };
    },
    validation(message, fields = []) {
        return { code: "VALIDATION", message, fields };
    },
    unauthorized(message, responseCode) {
        return responseCode
            ? { code: "UNAUTHORIZED", message, responseCode }
            : { code: "UNAUTHORIZED", message };
    },
    forbidden(reason, responseCode) {
        return responseCode
            ? { code: "FORBIDDEN", reason, responseCode }
            : { code: "FORBIDDEN", reason };
    },
    badRequest(message) {
        return { code: "BAD_REQUEST", message };
    },
    /**
     * 503 — a transient/upstream dependency (LLM, external API) failed or timed
     * out. Use for best-effort operations where failure is an expected
     * operational condition, NOT a server bug. Returning this instead of
     * throwing keeps the failure out of the unhandled-exception Sentry stream
     * while still telling the client to retry.
     */
    serviceUnavailable(message) {
        return { code: "SERVICE_UNAVAILABLE", message };
    }
};
// ─── Type guards ──────────────────────────────────────────
export function isOk(result) {
    return result.ok;
}
export function isErr(result) {
    return !result.ok;
}
// ─── HTTP status mapping ──────────────────────────────────
const ERROR_STATUS_MAP = {
    NOT_FOUND: 404,
    CONFLICT: 409,
    VALIDATION: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    BAD_REQUEST: 400,
    SERVICE_UNAVAILABLE: 503
};
/**
 * Maps an AppError code to an HTTP status code.
 * Used by the route layer to convert Result errors to HTTP responses.
 */
export function errorToHttpStatus(error) {
    return ERROR_STATUS_MAP[error.code];
}
//# sourceMappingURL=result.js.map